import { useState } from "react";
import axios from "axios";
import { Mail, Phone, MapPin, Briefcase, Camera, Send, Check, Instagram, Linkedin, Facebook } from "lucide-react";
import Seo from "../lib/seo";
import { CONTACT, SOCIAL } from "../lib/constants";
import PageHeader from "../components/PageHeader";
import Reveal from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TikTokIcon = ({ size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M16.5 0H12v15.5c0 1.66-1.34 3-3 3s-3-1.34-3-3 1.34-3 3-3c.34 0 .67.06.98.16V8.05c-.32-.04-.65-.05-.98-.05C5.13 8 2 11.13 2 15s3.13 7 7 7 7-3.13 7-7V8.5c1.5 1.1 3.34 1.74 5.34 1.74V6.06c-2.83 0-4.84-1.66-4.84-6.06z" />
    </svg>
);

const encode = (data) =>
    Object.keys(data)
        .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
        .join("&");

export default function ContactPage() {
    const [form, setForm] = useState({ first_name: "", last_name: "", email: "", subject: "", message: "", inquiry_type: "general" });
    const [status, setStatus] = useState("idle"); // idle | submitting | success | error
    const [errorMsg, setErrorMsg] = useState("");

    const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();

        // Client-side validation (in addition to native HTML5)
        const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRe.test(form.email)) {
            setStatus("error");
            setErrorMsg("Please enter a valid email address.");
            return;
        }

        setStatus("submitting");
        setErrorMsg("");

        // Detect environment: emergentagent preview & localhost = dev (use FastAPI backend);
        // any other host (jclawsc.org, *.netlify.app, custom domain) = production (use Netlify Forms).
        const host = typeof window !== "undefined" ? window.location.hostname : "";
        const isDevPreview = host.includes("emergentagent") || host === "localhost" || host === "127.0.0.1";

        try {
            if (isDevPreview) {
                // Dev preview: submit to FastAPI backend
                await axios.post(`${API}/contact`, form);
            } else {
                // Production (Netlify): submit to Netlify Forms via URL-encoded POST to "/"
                const response = await fetch("/", {
                    method: "POST",
                    headers: { "Content-Type": "application/x-www-form-urlencoded" },
                    body: encode({ "form-name": "contact", "bot-field": "", ...form }),
                });
                if (!response.ok) {
                    throw new Error(`Netlify form submission failed (status ${response.status}).`);
                }
            }

            setStatus("success");
            setForm({ first_name: "", last_name: "", email: "", subject: "", message: "", inquiry_type: "general" });
        } catch (err) {
            setStatus("error");
            setErrorMsg(err?.response?.data?.detail || err?.message || "Something went wrong. Please email us directly at Information@jclawsc.org.");
        }
    };

    return (
        <>
            <Seo
                title="Contact Us — We're Here To Connect"
                description="Reach out to the JC Law Foundation for general inquiries, media requests, partnership opportunities, or to share your story. Together, we can save lives."
                path="/contact"
            />

            <PageHeader
                overline="Contact Us"
                title="We're here to connect."
                subtitle="Supporter, medical professional, legislator, journalist, organization, or someone with a similar story — we're all in this together. Together, we can create safer environments and save lives."
            />

            {/* Contact banner image — full-width below page header */}
            <section className="bg-editorial pt-0" data-testid="contact-banner-image">
                <div className="relative w-full overflow-hidden">
                    <img
                        src="/images/contact-banner.png"
                        alt="Connect with the JC Law Foundation — CPR-trained hands, AED preparedness, and the heartbeat behind every life we work to save"
                        className="w-full h-auto block"
                        loading="lazy"
                    />
                </div>
            </section>

            <section className="bg-editorial py-24 md:py-32" data-testid="contact-section">
                <div className="max-w-[1300px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
                    {/* Get in touch */}
                    <Reveal className="lg:col-span-5 space-y-8">
                        <div>
                            <p className="overline mb-5 text-coral">Get In Touch</p>
                            <h2 className="font-serif text-4xl md:text-5xl text-navy-900 leading-tight">
                                Every second matters.
                            </h2>
                        </div>

                        <div className="space-y-6">
                            <div className="flex gap-4 items-start group" data-testid="contact-info-general">
                                <div className="w-10 h-10 border border-navy-900/20 group-hover:bg-navy-900 group-hover:text-editorial transition-colors flex items-center justify-center flex-shrink-0">
                                    <Mail size={18} />
                                </div>
                                <div>
                                    <p className="text-xs uppercase tracking-[0.22em] text-navy-700/70 mb-1">General Inquiries</p>
                                    <a href={`mailto:${CONTACT.general}`} className="text-navy-900 hover:text-coral">{CONTACT.general}</a>
                                </div>
                            </div>
                            <div className="flex gap-4 items-start group" data-testid="contact-info-press">
                                <div className="w-10 h-10 border border-navy-900/20 group-hover:bg-navy-900 group-hover:text-editorial transition-colors flex items-center justify-center flex-shrink-0">
                                    <Camera size={18} />
                                </div>
                                <div>
                                    <p className="text-xs uppercase tracking-[0.22em] text-navy-700/70 mb-1">Media & Press</p>
                                    <a href={`mailto:${CONTACT.press}`} className="text-navy-900 hover:text-coral">{CONTACT.press}</a>
                                </div>
                            </div>
                            <div className="flex gap-4 items-start group" data-testid="contact-info-partnership">
                                <div className="w-10 h-10 border border-navy-900/20 group-hover:bg-navy-900 group-hover:text-editorial transition-colors flex items-center justify-center flex-shrink-0">
                                    <Briefcase size={18} />
                                </div>
                                <div>
                                    <p className="text-xs uppercase tracking-[0.22em] text-navy-700/70 mb-1">Partnership Opportunities</p>
                                    <a href={`mailto:${CONTACT.partnership}`} className="text-navy-900 hover:text-coral">{CONTACT.partnership}</a>
                                </div>
                            </div>
                            <div className="flex gap-4 items-start group" data-testid="contact-info-phone">
                                <div className="w-10 h-10 border border-navy-900/20 group-hover:bg-navy-900 group-hover:text-editorial transition-colors flex items-center justify-center flex-shrink-0">
                                    <Phone size={18} />
                                </div>
                                <div>
                                    <p className="text-xs uppercase tracking-[0.22em] text-navy-700/70 mb-1">Phone</p>
                                    <a href={`tel:${CONTACT.phoneRaw}`} className="text-navy-900 hover:text-coral">{CONTACT.phone}</a>
                                </div>
                            </div>
                            <div className="flex gap-4 items-start group">
                                <div className="w-10 h-10 border border-navy-900/20 group-hover:bg-navy-900 group-hover:text-editorial transition-colors flex items-center justify-center flex-shrink-0">
                                    <MapPin size={18} />
                                </div>
                                <div>
                                    <p className="text-xs uppercase tracking-[0.22em] text-navy-700/70 mb-1">Based In</p>
                                    <span className="text-navy-900">{CONTACT.location}</span>
                                </div>
                            </div>
                        </div>

                        <div className="pt-6 border-t border-editorial-border">
                            <p className="overline mb-4 text-coral">Follow Us</p>
                            <div className="flex gap-3">
                                <a href={SOCIAL.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-10 h-10 border border-navy-900/20 hover:bg-navy-900 hover:text-editorial transition-colors flex items-center justify-center"><Instagram size={18} /></a>
                                <a href={SOCIAL.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="w-10 h-10 border border-navy-900/20 hover:bg-navy-900 hover:text-editorial transition-colors flex items-center justify-center"><TikTokIcon /></a>
                                <a href={SOCIAL.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-10 h-10 border border-navy-900/20 hover:bg-navy-900 hover:text-editorial transition-colors flex items-center justify-center"><Facebook size={18} /></a>
                                <a href={SOCIAL.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="w-10 h-10 border border-navy-900/20 hover:bg-navy-900 hover:text-editorial transition-colors flex items-center justify-center"><Linkedin size={18} /></a>
                            </div>
                            <p className="mt-3 text-sm text-navy-800/70">@JCLawInitiative</p>
                        </div>
                    </Reveal>

                    {/* Form */}
                    <Reveal delay={0.1} className="lg:col-span-7">
                        <div className="bg-white border border-editorial-border p-8 lg:p-12">
                            <p className="overline mb-3 text-coral">Send Us A Message</p>
                            <h2 className="font-serif text-3xl md:text-4xl text-navy-900 mb-8 leading-tight">
                                We'd love to hear from you.
                            </h2>

                            {status === "success" ? (
                                <div className="bg-navy-900 text-editorial p-8 text-center" data-testid="contact-success">
                                    <div className="w-14 h-14 rounded-full bg-coral mx-auto flex items-center justify-center mb-5">
                                        <Check className="w-7 h-7 text-white" />
                                    </div>
                                    <h3 className="font-serif text-2xl mb-3">Thank you.</h3>
                                    <p className="text-editorial/80">Your message has been received. We'll be in touch soon.</p>
                                    <button onClick={() => setStatus("idle")} className="mt-6 px-6 py-2.5 border border-editorial/40 hover:bg-editorial hover:text-navy-900 transition-colors text-sm">
                                        Send another
                                    </button>
                                </div>
                            ) : (
                                <form
                                    name="contact"
                                    method="POST"
                                    data-netlify="true"
                                    netlify-honeypot="bot-field"
                                    onSubmit={submit}
                                    className="space-y-5"
                                    data-testid="contact-form"
                                >
                                    <input type="hidden" name="form-name" value="contact" />
                                    <p className="hidden">
                                        <label>Don't fill this out: <input name="bot-field" /></label>
                                    </p>

                                    <div className="grid md:grid-cols-2 gap-5">
                                        <div>
                                            <label htmlFor="first_name" className="block text-xs uppercase tracking-[0.22em] text-navy-700/70 mb-2">First Name *</label>
                                            <input
                                                id="first_name"
                                                name="first_name"
                                                type="text"
                                                required
                                                value={form.first_name}
                                                onChange={update("first_name")}
                                                data-testid="contact-first-name"
                                                className="w-full px-4 py-3 bg-editorial-muted border border-editorial-border focus:border-navy-900 focus:outline-none text-navy-900 transition-colors"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="last_name" className="block text-xs uppercase tracking-[0.22em] text-navy-700/70 mb-2">Last Name *</label>
                                            <input
                                                id="last_name"
                                                name="last_name"
                                                type="text"
                                                required
                                                value={form.last_name}
                                                onChange={update("last_name")}
                                                data-testid="contact-last-name"
                                                className="w-full px-4 py-3 bg-editorial-muted border border-editorial-border focus:border-navy-900 focus:outline-none text-navy-900 transition-colors"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="email" className="block text-xs uppercase tracking-[0.22em] text-navy-700/70 mb-2">Email *</label>
                                        <input
                                            id="email"
                                            name="email"
                                            type="email"
                                            required
                                            value={form.email}
                                            onChange={update("email")}
                                            data-testid="contact-email"
                                            className="w-full px-4 py-3 bg-editorial-muted border border-editorial-border focus:border-navy-900 focus:outline-none text-navy-900 transition-colors"
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="inquiry_type" className="block text-xs uppercase tracking-[0.22em] text-navy-700/70 mb-2">Inquiry Type</label>
                                        <select
                                            id="inquiry_type"
                                            name="inquiry_type"
                                            value={form.inquiry_type}
                                            onChange={update("inquiry_type")}
                                            data-testid="contact-inquiry-type"
                                            className="w-full px-4 py-3 bg-editorial-muted border border-editorial-border focus:border-navy-900 focus:outline-none text-navy-900 transition-colors"
                                        >
                                            <option value="general">General Inquiry</option>
                                            <option value="partnership">Partnership Opportunity</option>
                                            <option value="volunteer">Volunteer Interest</option>
                                            <option value="media">Media / Press</option>
                                            <option value="donation">Donation Support</option>
                                            <option value="story">Share Your Story</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label htmlFor="subject" className="block text-xs uppercase tracking-[0.22em] text-navy-700/70 mb-2">Subject *</label>
                                        <input
                                            id="subject"
                                            name="subject"
                                            type="text"
                                            required
                                            value={form.subject}
                                            onChange={update("subject")}
                                            data-testid="contact-subject"
                                            className="w-full px-4 py-3 bg-editorial-muted border border-editorial-border focus:border-navy-900 focus:outline-none text-navy-900 transition-colors"
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="message" className="block text-xs uppercase tracking-[0.22em] text-navy-700/70 mb-2">Message *</label>
                                        <textarea
                                            id="message"
                                            name="message"
                                            rows={6}
                                            required
                                            value={form.message}
                                            onChange={update("message")}
                                            data-testid="contact-message"
                                            className="w-full px-4 py-3 bg-editorial-muted border border-editorial-border focus:border-navy-900 focus:outline-none text-navy-900 transition-colors resize-none"
                                        />
                                    </div>

                                    {status === "error" && (
                                        <p className="text-coral text-sm" data-testid="contact-error">{errorMsg}</p>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={status === "submitting"}
                                        data-testid="contact-submit"
                                        className="w-full bg-navy-900 hover:bg-navy-800 text-editorial font-medium py-4 flex items-center justify-center gap-3 transition-colors disabled:opacity-60"
                                    >
                                        {status === "submitting" ? "Sending…" : (<><Send className="w-4 h-4" /> Submit</>)}
                                    </button>
                                </form>
                            )}
                        </div>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
